import { CHANNELS } from "../../shared/channels";
import { CHIX_INBOX, CHIX_INBOX_NAME, boardKey } from "../../shared/task_status";
import { ChixBoard, TaskEntry, TaskSnapshot } from "../../shared/types";
import { call } from "../bridge";
import { flashToast } from "../dom";
import { t } from "../i18n";
import { buzz, flash } from "../tweens";

// chix boards: the tuner above the tasks list, the picker in the sticky editor and the sheet that names, renames
// and deletes a board. boards are per person and chix only, the bot never hears about them

export interface BoardChoice {
  key: string;
  name: string;
  plane: boolean;
}

let boards: ChixBoard[] = [];

export function setChixBoards(next: ChixBoard[] | undefined): void {
  boards = next ?? [];
}

// boards a chix task can sit on: the inbox first, then this person's own
export function chixBoards(): BoardChoice[] {
  return [{ key: CHIX_INBOX, name: CHIX_INBOX_NAME, plane: false }, ...boards.map((board) => ({ key: board.id, name: board.name, plane: false }))];
}

// every board in the tuner: chix ones, then the plane projects that have tasks
export function tunerBoards(tasks: TaskEntry[]): BoardChoice[] {
  const plane = [...new Set(tasks.filter((task) => task.source === "plane" && task.board).map((task) => task.board))].sort((a, b) => a.localeCompare(b));
  return [...chixBoards(), ...plane.map((name) => ({ key: `plane:${name}`, name, plane: true }))];
}

export const isChixBoard = (key: string): boolean => chixBoards().some((board) => board.key === key);

function cleanError(error: unknown): string {
  return String(error instanceof Error ? error.message : error).replace(/^.*Error: /, "");
}

function chip(label: string, on: boolean, extra = ""): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = `chip${extra ? ` ${extra}` : ""}${on ? " active" : ""}`;
  button.setAttribute("aria-pressed", String(on));
  button.textContent = label;
  return button;
}

function count(button: HTMLElement, value: number): void {
  const number = document.createElement("b");
  number.className = "chip-count";
  number.textContent = String(value);
  button.append(" ", number);
}

export interface TunerOptions {
  host: HTMLElement;
  tasks: TaskEntry[];
  pick: string;
  // open tasks per board, keyed like the tuner
  open: (key: string) => number;
  total: number;
  choose: (key: string) => void;
  changed: (snapshot: TaskSnapshot) => void;
}

// the row of board chips. the selected chix board gets a pencil key for renaming and deleting
export function renderTuner(options: TunerOptions): void {
  const { host, tasks, pick } = options;
  const all = chip(t("all"), pick === "all");
  count(all, options.total);
  all.addEventListener("click", () => options.choose("all"));
  const row: HTMLElement[] = [all];
  for (const board of tunerBoards(tasks)) {
    const on = board.key === pick;
    const button = chip(board.name, on, board.plane ? "board-chip plane" : "board-chip");
    if (board.plane) button.setAttribute("aria-label", t("{name}, plane project", { name: board.name }));
    count(button, options.open(board.key));
    button.addEventListener("click", () => options.choose(board.key));
    row.push(button);
    if (on && !board.plane && board.key !== CHIX_INBOX) {
      const edit = chip("✎", false, "board-edit");
      edit.setAttribute("aria-label", t("rename or delete {name}", { name: board.name }));
      edit.addEventListener("click", () => editBoard(board, tasks, options.changed));
      row.push(edit);
    }
  }
  const add = chip(`+ ${t("board")}`, false, "board-add");
  add.addEventListener("click", () => newBoard(options.changed));
  row.push(add);
  host.replaceChildren(...row);
  host.querySelector<HTMLElement>(".active")?.scrollIntoView({ block: "nearest", inline: "nearest" });
}

// the editor's picker: one chip per chix board plus a way to make one
export function renderBoardPicker(host: HTMLElement, current: string, pick: (key: string) => void): void {
  const known = isChixBoard(current) ? current : CHIX_INBOX;
  const row = chixBoards().map((board) => {
    const button = chip(board.name, board.key === known, "board-chip");
    button.addEventListener("click", () => {
      pick(board.key);
      renderBoardPicker(host, board.key, pick);
    });
    return button;
  });
  const add = chip(`+ ${t("board")}`, false, "board-add");
  add.addEventListener("click", () => newBoard((snapshot) => {
    setChixBoards(snapshot.meta?.boards);
    // a new board is picked in the tuner, so take it here as well
    const made = snapshot.meta?.pick ?? CHIX_INBOX;
    pick(made);
    renderBoardPicker(host, made, pick);
  }));
  host.replaceChildren(...row, add);
}

// ---------- the board sheet ----------

interface SheetOptions {
  title: string;
  name: string;
  note?: string;
  save: (name: string) => Promise<void>;
  remove?: () => Promise<void>;
}

function openBoardSheet(options: SheetOptions): void {
  const modal = document.createElement("div");
  modal.className = "modal board-sheet";
  const sheet = document.createElement("div");
  sheet.className = "modal-sheet panel stack";
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-label", options.title);
  const heading = document.createElement("h2");
  heading.textContent = options.title;
  const input = document.createElement("input");
  input.id = "board-sheet-name";
  input.maxLength = 40;
  input.autocomplete = "off";
  input.placeholder = t("board name");
  input.setAttribute("aria-label", t("board name"));
  input.value = options.name;
  sheet.append(heading, input);
  if (options.note) {
    const note = document.createElement("p");
    note.className = "hint";
    note.textContent = options.note;
    sheet.append(note);
  }
  const status = document.createElement("span");
  status.className = "hint";
  const keys = document.createElement("div");
  keys.className = "row";
  const close = () => modal.remove();
  if (options.remove) {
    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "pill board-remove";
    remove.textContent = t("delete board");
    let armed = false;
    let timer = 0;
    remove.addEventListener("click", async () => {
      if (!armed) {
        armed = true;
        remove.classList.add("armed");
        remove.textContent = t("sure? tap again");
        timer = window.setTimeout(() => {
          armed = false;
          remove.classList.remove("armed");
          remove.textContent = t("delete board");
        }, 3500);
        return;
      }
      window.clearTimeout(timer);
      try {
        await options.remove?.();
        buzz(12);
        close();
      } catch (error) {
        status.textContent = cleanError(error);
      }
    });
    keys.append(remove);
  }
  const gap = document.createElement("span");
  gap.className = "keys-gap";
  const cancel = document.createElement("button");
  cancel.type = "button";
  cancel.className = "pill";
  cancel.textContent = t("cancel");
  cancel.addEventListener("click", close);
  const save = document.createElement("button");
  save.type = "button";
  save.className = "pill accent";
  save.textContent = t("save");
  const submit = async () => {
    const name = input.value.trim();
    if (!name) {
      flash(input);
      return;
    }
    save.disabled = true;
    try {
      await options.save(name);
      close();
    } catch (error) {
      status.textContent = cleanError(error);
    } finally {
      save.disabled = false;
    }
  };
  save.addEventListener("click", () => void submit());
  input.addEventListener("keydown", (event) => {
    if (event.key === "Enter") void submit();
  });
  keys.append(gap, cancel, save);
  sheet.append(status, keys);
  modal.append(sheet);
  modal.addEventListener("click", (event) => {
    if (event.target === modal) close();
  });
  modal.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      close();
    }
  });
  document.body.append(modal);
  input.focus();
}

function newBoard(changed: (snapshot: TaskSnapshot) => void): void {
  openBoardSheet({
    title: t("new board"),
    name: "",
    save: async (name) => {
      const fresh = await call<TaskSnapshot>(CHANNELS.tasksBoardAdd, name);
      if (fresh?.tasks) changed(fresh);
    }
  });
}

function editBoard(board: BoardChoice, tasks: TaskEntry[], changed: (snapshot: TaskSnapshot) => void): void {
  const on = tasks.filter((task) => boardKey(task) === board.key).length;
  openBoardSheet({
    title: t("board"),
    name: board.name,
    note: t("{count} tasks on this board. Deleting the board moves them to {inbox}.", { count: on, inbox: CHIX_INBOX_NAME }),
    save: async (name) => {
      if (name === board.name) return;
      const fresh = await call<TaskSnapshot>(CHANNELS.tasksBoardRename, board.key, name);
      if (fresh?.tasks) changed(fresh);
    },
    remove: async () => {
      const fresh = await call<TaskSnapshot>(CHANNELS.tasksBoardRemove, board.key);
      if (fresh?.tasks) changed(fresh);
      flashToast(t("board deleted, its tasks are back in {inbox}", { inbox: CHIX_INBOX_NAME }));
    }
  });
}
